//* 면세계산서 발행 요청 모달 — 가상계좌·계좌입금 결제 건의 현금영수증을 취소하고
//* 면세계산서를 수동 발행해달라는 요청을 접수한다 (seller 접수 → admin 처리).
//* 사진·사업자번호·이메일 전부 optional — 고객이 사진만 준 경우 그대로 수용.
//* Requested/Rejected 상태에선 재요청이 기존 요청을 덮어쓴다(오타 교정). Done이면 400.
//* (스펙: docs/shop-order-seller-request-spec.md · 404 안내는 구서버 안전장치)

import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import {
  requestShopOrderTaxInvoice,
  ShopOrderTaxInvoiceRequestDto,
} from '@shared/api/shopOrder/shopOrder.controller';
import { shopOrderListForSellerUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { ShopOrderWithSellerRequests } from '@shared/api/shopOrder/shopOrder.interface';
import { sellerRequestStatusLabel } from '@shared/api/shopOrder/shopOrder.utils';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { PaymentState } from '@shared/types';
import {
  fileBucketUrl,
  readFileAsDataUrl,
  validateImageFile,
} from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { Image, Tag } from 'antd';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderWithSellerRequests;
  //* 목록 페이지 등 카드 밖에서 열었을 때 새로고침 콜백
  onSuccess?: () => void;
}

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

const requestErrorMessage = (e: unknown): string => {
  switch (statusOf(e)) {
    case 400:
      return '요청할 수 없는 상태입니다. (이미 발행 완료됐거나 결제완료 주문이 아님)';
    case 403:
      return '권한이 없습니다.';
    case 404:
      return '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.';
    case 409:
      return '요청 상태가 방금 바뀌었습니다. 새로고침 후 다시 확인해주세요.';
    default:
      return '면세계산서 요청에 실패했습니다.';
  }
};

const dateString = (date?: Date) =>
  formatDate(date, {
    dateSeparater: '.',
    contains: { hours: true, minutes: true },
  });

export const OrderTaxInvoiceRequestModal: FC<Props> =
  function OrderTaxInvoiceRequestModal({ openId, order, onSuccess }: Props) {
    const { sellerRole, assetRole } = useAuth();
    const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
    const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
    const queryClient = useQueryClient();

    const existing = order.taxInvoiceRequest;
    const isDone = existing?.status === 'Done';
    //* 현금영수증이 발행된 결제완료 건만 신규 요청 가능 (백엔드도 400으로 검증)
    const paidState = [PaymentState.PAID, PaymentState.CONFIRMED].includes(
      order.paymentDetail.status,
    );
    const canSubmit = !isDone && (paidState || !!existing);

    const [bizNumber, setBizNumber] = useState('');
    const [email, setEmail] = useState('');
    const [requestNote, setRequestNote] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string>();
    const [submitting, setSubmitting] = useState(false);

    //* 열리는 순간에만 기존 요청 내용으로 프리필(재요청 = 덮어쓰기). existing을 deps에 넣으면
    //* 편집 중 창 포커스 refetch로 order가 갱신될 때 입력값은 물론 방금 고른 사업자등록증
    //* 사진(file/preview)까지 초기화됨 — 열림 전환에만 의존한다.
    useEffect(() => {
      if (!open) return;
      setBizNumber(existing?.bizNumber ?? '');
      setEmail(existing?.email ?? '');
      setRequestNote(existing?.requestNote ?? '');
      setFile(null);
      setPreview(undefined);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onCloseModal = useCallback(() => {
      setOpen(false);
      history.back();
    }, [setOpen]);

    //* 기존 첨부 사진 — populate 여부에 따라 Image 객체(url 표시 가능) 또는 _id 문자열
    const existingImage = useMemo(() => {
      const raw = existing?.bizRegImage;
      if (!raw) return { id: undefined as string | undefined, url: undefined };
      if (typeof raw === 'string') return { id: raw, url: undefined };
      return { id: raw._id, url: raw.url };
    }, [existing?.bizRegImage]);

    const onSelectFile = async (e: ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      e.target.value = '';
      if (!selected || !validateImageFile(selected)) return;
      setFile(selected);
      setPreview(await readFileAsDataUrl(selected));
    };

    const onSubmit = useCallback(async () => {
      const dto: ShopOrderTaxInvoiceRequestDto = {
        bizRegImage: undefined,
        bizNumber: bizNumber.trim() || undefined,
        email: email.trim() || undefined,
        requestNote: requestNote.trim() || undefined,
      };
      if (!file && !existingImage.id && !dto.bizNumber && !dto.email && !dto.requestNote) {
        notificateError(
          notifiacationInstance,
          '요청 내용(사진·사업자번호·이메일·메모)을 하나 이상 입력해주세요.',
        );
        return;
      }
      setSubmitting(true);
      try {
        //* 새 사진이 있으면 선업로드, 없으면 기존 첨부 유지(재요청이 덮어쓰기라서 다시 보내야 함)
        if (file) {
          const image = await postImage(file, assetRole());
          dto.bizRegImage = image._id;
        } else {
          dto.bizRegImage = existingImage.id;
        }
        await requestShopOrderTaxInvoice(order._id, dto, sellerRole());
        queryClient.invalidateQueries(shopOrderListForSellerUrl(sellerRole()));
        notificateSuccess(notifiacationInstance, '면세계산서 발행 요청 접수 완료');
        onSuccess?.();
        onCloseModal();
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, requestErrorMessage(err));
      } finally {
        setSubmitting(false);
      }
    }, [
      file,
      existingImage.id,
      bizNumber,
      email,
      requestNote,
      order._id,
      sellerRole,
      assetRole,
      queryClient,
      notifiacationInstance,
      onSuccess,
      onCloseModal,
    ]);

    return (
      <Modal openId={openId}>
        <div className="space-y-4">
          <div>
            <p className="text-lg font-bold">면세계산서 발행 요청</p>
            <p className="break-keep pt-1 text-sm text-gray-600">
              가상계좌·계좌입금 결제 건의 현금영수증을 취소하고 면세계산서를 수동
              발행하는 요청입니다. 고객에게 받은 자료 중 있는 것만 입력하면 되고, 실제
              발행·완료 처리는 운영사가 합니다.
            </p>
          </div>

          {/* 주문 요약 */}
          <div className="rounded-md border bg-gray-50 p-3 text-sm">
            <p className="font-medium">{order.orderDetail?.shopItemEmbed?.name}</p>
            <p className="text-gray-700">
              결제 {order.paymentDetail.paymentByCash.toLocaleString()}원 ·{' '}
              {order.paymentDetail.method}
              <span className="text-xs text-gray-500" title={`주문번호: ${order._id}`}>
                {' '}
                · 주문 {order._id.slice(-6)}
              </span>
            </p>
          </div>

          {/* 기존 요청 상태 */}
          {existing && (
            <div className="rounded-md border p-3 text-sm">
              <p className="flex items-center gap-2">
                <Tag
                  className={
                    existing.status === 'Done'
                      ? 'border-green-600 text-green-700'
                      : existing.status === 'Rejected'
                      ? 'border-red-500 text-red-600'
                      : 'border-amber-500 text-amber-700'
                  }
                >
                  {sellerRequestStatusLabel[existing.status]}
                </Tag>
                <span className="text-gray-600">
                  요청일 {dateString(existing.requestedAt)}
                </span>
              </p>
              {existing.status === 'Done' && (
                <div className="pt-1 text-gray-700">
                  <p>처리일 {dateString(existing.processedAt)}</p>
                  {existing.cashReceiptCanceledAt && (
                    <p>현금영수증 취소 {dateString(existing.cashReceiptCanceledAt)}</p>
                  )}
                  {existing.resultNote && <p>처리 메모: {existing.resultNote}</p>}
                </div>
              )}
              {existing.status === 'Rejected' && (
                <p className="break-keep pt-1 text-red-600">
                  반려 사유: {existing.resultNote || '(사유 미기재)'} — 내용을 수정해
                  다시 요청할 수 있습니다.
                </p>
              )}
              {existing.status === 'Requested' && (
                <p className="break-keep pt-1 text-xs text-gray-500">
                  처리 대기 중입니다. 다시 접수하면 기존 요청 내용을 덮어씁니다.
                </p>
              )}
            </div>
          )}

          {!canSubmit && !existing && (
            <p className="break-keep text-sm text-red-600">
              결제완료(입금확인) 주문만 요청할 수 있습니다.
            </p>
          )}

          {/* 입력 필드 */}
          {!isDone && (
            <div className="space-y-2 text-sm">
              <div className="space-y-1">
                <p className="font-medium">사업자등록증 사진</p>
                {preview ? (
                  <Image
                    src={preview}
                    alt="사업자등록증 미리보기"
                    className="max-h-60 w-full rounded-md object-contain"
                  />
                ) : existingImage.url ? (
                  <Image
                    src={fileBucketUrl(existingImage.url, 'w200')}
                    alt="기존 사업자등록증"
                    preview={{ src: fileBucketUrl(existingImage.url, 'raw') }}
                    className="max-h-60 rounded-md object-contain"
                  />
                ) : existingImage.id ? (
                  <p className="text-xs text-gray-500">
                    기존 첨부 사진이 유지됩니다. (새 사진 선택 시 교체)
                  </p>
                ) : null}
                <label className="inline-block cursor-pointer rounded-md border border-gray-300 px-3 py-1.5 hover:bg-gray-50">
                  {file || existingImage.id ? '사진 교체' : '사진 선택'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onSelectFile}
                  />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">사업자번호</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="123-45-67890 (사진으로 갈음 시 생략)"
                  value={bizNumber}
                  onChange={(e) => setBizNumber(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">이메일</label>
                <input
                  type="email"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="발행본 수신 이메일 (없으면 생략)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="w-20 shrink-0 font-medium">메모</label>
                <input
                  type="text"
                  className="w-full rounded border border-gray-300 px-2 py-1"
                  placeholder="요청 경위 등 (선택)"
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              className="button-rectangle h-10 flex-1 border border-gray-300"
              onClick={onCloseModal}
            >
              닫기
            </Button>
            {!isDone && (
              <Button
                className="button-rectangle h-10 flex-1 theme-text-4 theme-bg-main"
                disabled={submitting || !canSubmit}
                onClick={onSubmit}
              >
                {submitting
                  ? '접수 중...'
                  : existing
                  ? '다시 요청 (덮어쓰기)'
                  : '요청 접수'}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    );
  };
